import {
	Asset,
	Button,
	CopyIcon,
	FONT_COLOR,
	FONT_SIZE,
	PaddedContainer,
	SPACING,
	Text,
	genClassName,
	isFunction,
	DotSwitcher,
} from "@disco/disco_core";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import pluralize from "pluralize";
import "./lead-gen-action.css";
import { OFFER_TYPE, WIDGET_TYPE, WIDGET_VERSION } from "../../conf";
import {
	BRAND_CLICK_TARGET,
	WIDGET_ELEMENT_CLICK_TARGET,
} from "../../event_conf";
import useData from "../../hooks/useData";
import useTimer from "../../hooks/useTimer";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import windowRedirect from "../../utils/windowRedirect";
import getParsedRedirectUrl from "../../utils/getParsedRedirectUrl";
import accessibilityProvider from "../../utils/accessibilityProvider";
import copyClipboard from "../../utils/copyClipboard";
import useExperience from "../../hooks/useExperience";
import useSendOfferEmail from "../../hooks/useSendOfferEmail";
import useDimensions from "../../hooks/useDimensions";
import useLeadGenV0Config, {
	LEAD_GEN_FEATURE,
} from "../../hooks/useLeadGenV0Config";
import useShopTheme from "../../hooks/useShopTheme";
import { SNOWPLOW_SCHEMA_DATA_KEY } from "../../utils/snowplowEventMigrator";
import brandOfferEntityMapper from "../../utils/brandOfferEntityMapper";
import inPreview from "../../utils/inPreview";
import useCustomization, {
	CUSTOMIZATION_DETAIL,
	CUSTOMIZATION_TYPE,
} from "../../hooks/useCustomization";
import { NextOfferButton } from "../EngageWidget/EngageOfferFooter";

const LeadGenActionNav = ({
	activeIdx,
	minIdx,
	maxIdx,
	btnProps,
	onNext,
	defaultEventProps,
	onPrev,
	brand,
	isOfferClaimed = false,
	isActionAnimating = false,
}) => {
	const track = useTrack();

	const handleNext = () => {
		if (inPreview) return;
		track(eventTypes.WIDGET_ELEMENT_CLICK, {
			...defaultEventProps,
			[SNOWPLOW_SCHEMA_DATA_KEY]: {
				...brandOfferEntityMapper({ brand }),
				horizontalPosition: activeIdx,
			},
			initiatingTarget: WIDGET_ELEMENT_CLICK_TARGET.INLINE_NAV_FORWARD,
		});
		if (!isFunction(onNext)) return;
		onNext();
	};

	const handlePrev = () => {
		if (inPreview) return;
		track(eventTypes.WIDGET_ELEMENT_CLICK, {
			...defaultEventProps,
			[SNOWPLOW_SCHEMA_DATA_KEY]: {
				...brandOfferEntityMapper({ brand }),
				horizontalPosition: activeIdx,
			},
			initiatingTarget: WIDGET_ELEMENT_CLICK_TARGET.INLINE_NAV_BACKWARD,
		});
		if (!isFunction(onPrev)) return;
		onPrev();
	};

	const { isFeatureActive, getFeatureConfig, isLargeWidget } =
		useLeadGenV0Config();

	const isActionModuleActive = isFeatureActive({
		feature: LEAD_GEN_FEATURE.ACTION_OFFER,
	});

	const { SHOULD_ANIMATE_NAV_TRANSITION } = getFeatureConfig({
		feature: LEAD_GEN_FEATURE.ACTION_BUTTON,
	});

	const shouldStyleBtn = isOfferClaimed && !isActionAnimating;

	const prevNavBtnStyle =
		shouldStyleBtn && activeIdx === maxIdx ? btnProps?.style : undefined;

	const nextNavBtnStyle = shouldStyleBtn ? btnProps?.style : undefined;

	const layoutAnimationProps = SHOULD_ANIMATE_NAV_TRANSITION
		? {
				motionElement: inPreview ? false : true,
				layout: true,
				transition: { duration: 0.11 },
		  }
		: {};

	const { applyCustomizationOrFallback } = useCustomization();

	const prevBtnCopy = applyCustomizationOrFallback(
		CUSTOMIZATION_DETAIL[CUSTOMIZATION_TYPE.CHECKOUT]
			.WIDGET_NAV_BACKWARD_CTA_TEXT,
		"Previous Offer"
	);
	const nextBtnCopy = applyCustomizationOrFallback(
		CUSTOMIZATION_DETAIL[CUSTOMIZATION_TYPE.CHECKOUT]
			.WIDGET_NAV_FORWARD_CTA_TEXT,
		"Next Offer"
	);

	return (
		<PaddedContainer
			marginTop={
				isActionModuleActive && isLargeWidget
					? SPACING.REGULAR
					: SPACING.SMALL
			}
			flexContent
			className="lead-gen-action-nav"
		>
			{activeIdx > minIdx && (
				<Button
					{...btnProps}
					style={prevNavBtnStyle}
					className={genClassName({
						base: "lead-gen-action-btn",
					})}
					onClick={handlePrev}
					data-testid="lead-gen-action-btn-prev"
					key="lead-gen-action-btn-prev"
					{...layoutAnimationProps}
				>
					{prevBtnCopy}
				</Button>
			)}
			{(activeIdx < maxIdx || inPreview) && (
				<Button
					{...btnProps}
					style={nextNavBtnStyle}
					className={genClassName({
						base: "lead-gen-action-btn",
					})}
					onClick={handleNext}
					data-testid="lead-gen-action-btn-next"
					key="lead-gen-action-btn-next"
					{...layoutAnimationProps}
				>
					{nextBtnCopy}
				</Button>
			)}
		</PaddedContainer>
	);
};
const LeadGenActionOffer = ({ brand, isOfferClaimed }) => {
	const { isLargeWidget } = useLeadGenV0Config();
	const {
		incentive_heading: incentiveHeading,
		incentive_code: incentiveCode,
		incentive_subheading: incentiveSubheading,
		incentive_type: incentiveType,
	} = brand;
	return (
		<PaddedContainer
			marginBottom={isLargeWidget ? SPACING.REGULAR : SPACING.SMALL}
		>
			<Text
				size={isLargeWidget ? undefined : FONT_SIZE.SUB_TITLE} // custom size specified in CSS
				{...accessibilityProvider({
					name: incentiveHeading,
				})}
				className="lead-gen-action-offer-heading"
			>
				{isOfferClaimed
					? `You've got ${incentiveHeading}`
					: incentiveHeading}
			</Text>
			<Text
				size={isLargeWidget ? FONT_SIZE.SUB_TITLE : FONT_SIZE.LG_BODY}
				marginTop={SPACING.MICRO}
				{...accessibilityProvider({
					name:
						isOfferClaimed &&
						incentiveType === OFFER_TYPE.PROMO_CODE
							? `Code sent to email: ${incentiveCode}`
							: incentiveSubheading,
				})}
			>
				{isOfferClaimed && incentiveType === OFFER_TYPE.PROMO_CODE ? (
					<>
						Use code: <b>{incentiveCode}</b>
					</>
				) : (
					incentiveSubheading || "On your next purchase"
				)}
			</Text>
		</PaddedContainer>
	);
};

const LeadGenActionButtonCopy = ({
	isActionAnimating,
	shouldAnimateFill,
	brand,
	incentiveType,
	isOfferClaimed,
	shouldApplyCtaHighlightStyle,
	isNurtureV1,
	showCopiedText,
}) => {
	const { applyCustomizationOrFallback } = useCustomization();
	const { activeWidgetType } = useExperience();
	const { getFeatureConfig } = useLeadGenV0Config();
	const { SHOULD_SHOW_COPY_ICON: canShowCopyIcon } = getFeatureConfig({
		feature: LEAD_GEN_FEATURE.ACTION_BUTTON,
	});

	const {
		WIDGET_CLAIMED_NO_PROMO_CTA_TEXT,
		WIDGET_CLAIMED_PROMO_CTA_TEXT,
		WIDGET_UNCLAIMED_NO_PROMO_CTA_TEXT,
		WIDGET_UNCLAIMED_PROMO_CTA_TEXT,
	} = CUSTOMIZATION_DETAIL[CUSTOMIZATION_TYPE.CHECKOUT];

	// Animating State
	if (isActionAnimating && shouldAnimateFill)
		return `Taking you to ${brand.name}`;

	if (isOfferClaimed) {
		// Claimed state => No Promo code
		if (incentiveType === OFFER_TYPE.NO_PROMO_CODE)
			return applyCustomizationOrFallback(
				WIDGET_CLAIMED_NO_PROMO_CTA_TEXT,
				activeWidgetType === WIDGET_TYPE.ENGAGE
					? "Visit shop"
					: "Redeem onsite"
			);
		// Claimed state => Promo code

		const shouldShowCopyIcon =
			!isNurtureV1 &&
			canShowCopyIcon &&
			activeWidgetType !== WIDGET_TYPE.ENGAGE;

		return (
			<>
				{shouldShowCopyIcon && (
					<Asset
						size={20}
						marginRight={SPACING.TINY}
						color={
							!shouldApplyCtaHighlightStyle && FONT_COLOR.WHITE
						}
						data-testid="lead-gen-action-btn-primary-asset"
					>
						<CopyIcon />
					</Asset>
				)}
				{showCopiedText
					? "Copied!"
					: applyCustomizationOrFallback(
							WIDGET_CLAIMED_PROMO_CTA_TEXT,
							"Copy code and shop"
					  )}
			</>
		);
	}

	// Unclaimed state
	if (incentiveType === OFFER_TYPE.NO_PROMO_CODE)
		return applyCustomizationOrFallback(
			WIDGET_UNCLAIMED_NO_PROMO_CTA_TEXT,
			"Redeem onsite"
		);

	// Unclaimed state => Promo code
	return applyCustomizationOrFallback(
		WIDGET_UNCLAIMED_PROMO_CTA_TEXT,
		"Claim Offer"
	);
};

export const LeadGenActionButton = ({
	brand,
	isOfferClaimed,
	btnProps,
	defaultEventProps,
	onOfferClaimed,
	onRedirect,
	activeIdx,
	isActionAnimating,
	setIsActionAnimating,
	shouldApplyCtaHighlightStyle,
}) => {
	const { isNurtureV1, activeWidgetType } = useExperience();
	const FILL_ANIMATION_DURATION = 950;

	const {
		incentive_type: incentiveType,
		incentive_landing_page_url: incentiveLandingPageUrl,
	} = brand;

	const { is_anon_recommendation: isAnonymousRecommendation } =
		useData() || {};

	const track = useTrack();

	const [, sendOfferEmail] = useSendOfferEmail({ brandId: brand?.id });

	const [showCopiedText, setShowCopiedText] = useState(false);
	const claimOfferTimeoutRef = useRef(null);

	const { getFeatureConfig } = useLeadGenV0Config();
	const { SHOULD_ANIMATE_FILL } = getFeatureConfig({
		feature: LEAD_GEN_FEATURE.ACTION_BUTTON,
	});

	const canAnimateFill =
		SHOULD_ANIMATE_FILL || activeWidgetType === WIDGET_TYPE.ENGAGE;

	const shouldAnimateFill =
		canAnimateFill && incentiveType === OFFER_TYPE.PROMO_CODE;

	const offerRedirectUrl = getParsedRedirectUrl({
		brand,
		redirectUrl: incentiveLandingPageUrl,
	});

	const redirectToBrand = () => {
		windowRedirect({
			url: offerRedirectUrl,
		});
	};

	const copyAndRedirect = () => {
		if (incentiveType === OFFER_TYPE.PROMO_CODE)
			copyClipboard(brand?.incentive_code);
		redirectToBrand();
	};

	const resetClaimAnimationFill = useCallback(() => {
		if (!claimOfferTimeoutRef.current) return;
		setIsActionAnimating(false);
		clearTimeout(claimOfferTimeoutRef.current);
		claimOfferTimeoutRef.current = null;
	}, [setIsActionAnimating]);

	useEffect(() => {
		resetClaimAnimationFill();
	}, [activeIdx, resetClaimAnimationFill]);

	const handleClaimAnimationFill = () => {
		if (!shouldAnimateFill) return;
		setIsActionAnimating(true);
		if (incentiveType === OFFER_TYPE.PROMO_CODE)
			copyClipboard(brand?.incentive_code);
		claimOfferTimeoutRef.current = setTimeout(() => {
			setIsActionAnimating(false);
			claimOfferTimeoutRef.current = null;
			redirectToBrand();
		}, FILL_ANIMATION_DURATION);
	};

	const isOutBoundClick =
		isOfferClaimed ||
		shouldAnimateFill ||
		incentiveType === OFFER_TYPE.NO_PROMO_CODE; // Offer claimed or single click redirect or no promo code

	const handleClaim = () => {
		if (showCopiedText) return;
		track(eventTypes.CLICK_BRAND, {
			...defaultEventProps,
			[SNOWPLOW_SCHEMA_DATA_KEY]: brandOfferEntityMapper({ brand }),
			brand_redirect_link: offerRedirectUrl,
			initiatingTarget: BRAND_CLICK_TARGET.INLINE_ACTION_BUTTON,
			isInternalClick: !isOutBoundClick,
		});

		if (isActionAnimating) resetClaimAnimationFill(); // To handle duplicate click when already animating

		if (isOfferClaimed) {
			if (!isNurtureV1) {
				copyAndRedirect();
				return;
			}
			setShowCopiedText(true);
			setTimeout(() => {
				copyAndRedirect();
				setShowCopiedText(false);
				if (isFunction(onRedirect)) onRedirect();
			}, 342);
			return;
		}

		const shouldDisableEmail = inPreview || isAnonymousRecommendation;
		if (!shouldDisableEmail) sendOfferEmail();

		if (typeof onOfferClaimed === "function") onOfferClaimed();

		if (incentiveType === OFFER_TYPE.NO_PROMO_CODE)
			return redirectToBrand();

		handleClaimAnimationFill();
	};

	const actionBtnStyle =
		isActionAnimating || !isOfferClaimed ? btnProps?.style : undefined;

	return (
		<Button
			{...btnProps}
			style={{
				...actionBtnStyle,
				"--animation-duration": `${FILL_ANIMATION_DURATION}ms`,
			}}
			className={genClassName({
				base: "lead-gen-action-btn",
				conditionals: {
					"lead-gen-action-btn-animate-fill":
						shouldAnimateFill && isActionAnimating,
					"lead-gen-action-btn-filled": !shouldApplyCtaHighlightStyle,
				},
			})}
			onClick={handleClaim}
			data-testid="lead-gen-action-btn-primary"
			hPadding={isNurtureV1 ? SPACING.SMALL : undefined}
		>
			<LeadGenActionButtonCopy
				isActionAnimating={isActionAnimating}
				shouldAnimateFill={shouldAnimateFill}
				brand={brand}
				incentiveType={incentiveType}
				isOfferClaimed={isOfferClaimed}
				shouldApplyCtaHighlightStyle={shouldApplyCtaHighlightStyle}
				isNurtureV1={isNurtureV1}
				showCopiedText={showCopiedText}
			/>
		</Button>
	);
};

const LeadGenAction = ({
	className = "",

	brand,
	defaultEventProps,
	isOfferClaimed = false,
	onOfferClaimed,
	onRedirect,

	activeIdx = 0,
	minIdx = 0,
	maxIdx = 0,
	onNext,
	onPrev,

	setActiveOfferIdx,
	isFirstOffer,
	isLastOffer,
	TOTAL_OFFERS,
	mock = false,
	triggerWidgetResize = () => {},
	setShowOfferSlot = () => {},

	...rest
}) => {
	const { isNurtureV1, activeWidgetVersion } = useExperience();
	const { isLargeWidget } = useDimensions();
	const [hasLooped, setHasLooped] = useState(false);
	const { isFeatureActive, getFeatureConfig } = useLeadGenV0Config();

	const isWidgetVersion07 =
		activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.7];

	const hasNextOfferButton = isFeatureActive({
		feature: LEAD_GEN_FEATURE.NEXT_OFFER_BUTTON,
	});

	const [showNextOfferButton, setShowNextOfferButton] =
		useState(hasNextOfferButton);

	const [isActionAnimating, setIsActionAnimating] = useState(false);

	const NEXT_OFFER_DELAY = 3;

	const timerPlaying = !isFirstOffer && isWidgetVersion07 && !isOfferClaimed;

	const { ticks, reset } = useTimer({
		numTicks: NEXT_OFFER_DELAY,
		playing: timerPlaying,
		onComplete: () => setShowNextOfferButton(true),
	});

	const isActionModuleActive =
		isFeatureActive({ feature: LEAD_GEN_FEATURE.ACTION_OFFER }) ||
		isNurtureV1;

	const shouldRenderActionNav =
		isFeatureActive({
			feature: LEAD_GEN_FEATURE.ACTION_NAV,
		}) || mock;

	const {
		backgroundColor: shopBackgroundColor,
		foregroundColor: shopForegroundColor,
		isConfigured: isShopThemeConfigured,
	} = useShopTheme();

	const ctaHighlightStyle = {
		"--bg": isShopThemeConfigured
			? shopBackgroundColor
			: "var(--disco-facebook)",
		"--color": isShopThemeConfigured
			? shopForegroundColor
			: "var(--disco-text-white)",
		"--border-color": "transparent",
	};

	const { SHOULD_HIGHLIGHT_CTA: shouldApplyCtaHighlightStyle } =
		getFeatureConfig({
			feature: LEAD_GEN_FEATURE.ACTION_BUTTON,
		});

	const btnProps = {
		secondary: true,
		rounded: false,
		centerContent: true,
		...accessibilityProvider({
			role: "button",
		}),
		style: shouldApplyCtaHighlightStyle ? ctaHighlightStyle : undefined,
	};

	const handleOnNext = () => {
		if (
			activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.65]
		) {
			if (isLastOffer) {
				setActiveOfferIdx(0);
				setHasLooped(true);
			}
		}

		if (isWidgetVersion07) {
			if (isLastOffer) {
				setShowOfferSlot(false);
			} else {
				reset();
				setShowNextOfferButton(false);
			}
		}
	};

	const secondsUntilNextOffer = NEXT_OFFER_DELAY - ticks;

	const getShouldShowDotSwitcher = () => {
		if (
			!isFeatureActive({
				feature: LEAD_GEN_FEATURE.DOT_SWITCHER,
			})
		) {
			return false;
		}

		if (
			activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.65]
		) {
			return hasLooped || !isFirstOffer;
		}

		if (isWidgetVersion07) {
			return (
				!isFirstOffer && (secondsUntilNextOffer === 0 || !timerPlaying)
			);
		}

		return true;
	};

	const shouldShowDotSwitcher = getShouldShowDotSwitcher();

	useEffect(() => {
		triggerWidgetResize();
	}, [triggerWidgetResize, shouldShowDotSwitcher]);

	useEffect(() => {
		// Show next offer button after animation completes for offers with promo code
		// Offers with no promo code will immediately show next offer button since `isActionAnimating` stays `false`
		if (hasNextOfferButton && isOfferClaimed && !isActionAnimating) {
			setShowNextOfferButton(true);
		}
	}, [hasNextOfferButton, isOfferClaimed, isActionAnimating]);

	const getNextOfferAvailableInText = () => {
		const pluralizedSeconds = pluralize(
			"second",
			secondsUntilNextOffer,
			true
		);

		if (isLastOffer) {
			return `or dismiss the offer in ${pluralizedSeconds}`;
		}

		return `or unlock the next offer in ${pluralizedSeconds}`;
	};

	return (
		<>
			<PaddedContainer
				className={genClassName({
					base: "lead-gen-action",
					additional: className,
				})}
				data-testid={
					isNurtureV1 ? "lead-gen-v1-action" : "lead-gen-action"
				}
				{...rest}
			>
				<PaddedContainer
					className={genClassName({
						base: "lead-gen-action-module",
						conditionals: {
							"lead-gen-action-module-active":
								isActionModuleActive,
						},
					})}
				>
					{isActionModuleActive && (
						<LeadGenActionOffer
							brand={brand}
							isOfferClaimed={isOfferClaimed}
						/>
					)}

					<PaddedContainer
						style={{
							display: "flex",
							alignItems: "center",
							gap: isLargeWidget
								? "var(--disco-spacing-sm)"
								: "var(--disco-spacing-tn)",
						}}
					>
						<motion.div
							layout
							style={{ flex: 1 }}
							transition={{ duration: 0.2, ease: "easeOut" }}
						>
							<LeadGenActionButton
								brand={brand}
								isOfferClaimed={isOfferClaimed}
								btnProps={btnProps}
								defaultEventProps={defaultEventProps}
								onOfferClaimed={onOfferClaimed}
								onRedirect={onRedirect}
								activeIdx={activeIdx}
								isActionAnimating={isActionAnimating}
								setIsActionAnimating={setIsActionAnimating}
								shouldApplyCtaHighlightStyle={
									shouldApplyCtaHighlightStyle
								}
							/>
						</motion.div>

						<AnimatePresence>
							{showNextOfferButton && (
								<motion.div
									initial={{
										opacity: 0,
										scale: 0.8,
										width: 0,
									}}
									animate={{
										opacity: 1,
										scale: 1,
										width: "auto",
									}}
									exit={{
										opacity: 0,
										scale: 0.8,
										width: 0,
									}}
									transition={{
										duration: 0.2,
										ease: "easeOut",
									}}
								>
									<NextOfferButton
										isActionAnimating={isActionAnimating}
										btnProps={btnProps}
										onClick={handleOnNext}
									/>
								</motion.div>
							)}
						</AnimatePresence>
					</PaddedContainer>
				</PaddedContainer>
				{shouldRenderActionNav && (
					<LeadGenActionNav
						activeIdx={activeIdx}
						minIdx={minIdx}
						maxIdx={maxIdx}
						onNext={onNext}
						onPrev={onPrev}
						brand={brand}
						defaultEventProps={defaultEventProps}
						btnProps={btnProps}
						isOfferClaimed={isOfferClaimed}
						isActionAnimating={isActionAnimating}
					/>
				)}
			</PaddedContainer>

			{shouldShowDotSwitcher ? (
				<PaddedContainer
					centerContent
					style={{
						height: "24px",
						display: "flex",
						flexDirection: "column",
						justifyContent: "flex-end",
					}}
				>
					<DotSwitcher
						viewOnly
						className="engage-widget-offers-navigator-dot-switcher"
						dots={TOTAL_OFFERS}
						active={activeIdx}
						hMarginAuto
					/>
				</PaddedContainer>
			) : isWidgetVersion07 && !isFirstOffer ? (
				<PaddedContainer
					centerContent
					style={{
						height: "24px",
						display: "flex",
						flexDirection: "column",
						justifyContent: "flex-end",
					}}
				>
					<Text color="mid" style={{ fontStyle: "italic" }}>
						{getNextOfferAvailableInText()}
					</Text>
				</PaddedContainer>
			) : null}
		</>
	);
};
export default LeadGenAction;
